/**
 * useFormPayload - Transform form data to API request payload
 *
 * Converts camelCase form data into snake_case API format
 * matching Python API's IncorporateReservationRequest Pydantic model
 */

// Re-use shared types from DI payload
import type {
  AddressPayload,
  ApplicationDetailsPayload,
  DirectorPayload,
  ShareholderPayload,
  InformationToAgencyPayload,
} from '../../direct-incorporation/composables/useFormPayload';

export type {
  AddressPayload,
  ApplicationDetailsPayload,
  DirectorPayload,
  ShareholderPayload,
  InformationToAgencyPayload,
};

/**
 * Matches: IncorporateReservationRequest (Python)
 */
export interface IncorporateReservationPayload {
  credential_id?: string;
  session_id: string;
  reserved_entity_number?: string;
  reserved_entity_name: string;
  business_codes: string[];
  application_details: ApplicationDetailsPayload;
  directors: DirectorPayload[];
  shareholders: ShareholderPayload[];
  information_to_agency?: InformationToAgencyPayload;
  dry_run?: boolean;
}

// ===== Helpers =====

const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

/**
 * Convert "DD MMMM YYYY" (e.g., "11 JULY 1983") to "YYYY-MM-DD" for Python API
 */
function toISODate(dateStr: string): string {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const match = dateStr.match(/^(\d{1,2})\s+([A-Z]+)\s+(\d{4})$/);
  if (match) {
    const monthIdx = MONTH_NAMES.indexOf(match[2]);
    if (monthIdx >= 0) {
      const day = match[1].padStart(2, '0');
      const month = String(monthIdx + 1).padStart(2, '0');
      return `${match[3]}-${month}-${day}`;
    }
  }
  return dateStr;
}

/**
 * Strip non-digit characters from phone/fax numbers (SSM requires digits only)
 */
function digitsOnly(value: string | undefined): string {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

/**
 * Convert address object to API payload
 */
function toAddressPayload(addr: Record<string, any>): AddressPayload {
  return {
    country: addr.country || 'MALAYSIA',
    state: addr.state || '',
    city: addr.city || '',
    post_code: addr.postcode || addr.post_code || '',
    street01: addr.street01 || '',
    street02: addr.street02 || '',
    street03: addr.street03 || '',
    office_number: digitsOnly(addr.officeNumber || addr.office_number),
    fax_number: digitsOnly(addr.faxNumber || addr.fax_number),
  };
}

/**
 * Build the full API payload from form data
 * Matches Python API's IncorporateReservationRequest exactly
 */
export async function buildPayload(
  formData: Record<string, any>,
  sessionId: string,
  reservedEntityName: string,
  reservedEntityNumber?: string,
  credentialId?: string
): Promise<IncorporateReservationPayload> {
  const appDetails = formData.applicationDetails || {};
  const infoAgency = formData.informationToAgency || {};

  // Addresses
  const addresses = appDetails._addresses || {};
  const regAddr = toAddressPayload(addresses.reg || {});
  const bizAddr = toAddressPayload(addresses.biz || {});

  const sameAsAbove = !addresses.biz ||
    (addresses.biz.street01 === addresses.reg?.street01 &&
     addresses.biz.state === addresses.reg?.state &&
     addresses.biz.city === addresses.reg?.city);

  const applicationDetails: ApplicationDetailsPayload = {
    business_description: appDetails.businessDescription || '',
    registered_address: regAddr,
    business_address: sameAsAbove ? undefined : bizAddr,
    email: appDetails.appEmail || '',
    same_as_above: sameAsAbove,
  };

  // Directors
  const directorsPayload: DirectorPayload[] = (formData.directors || []).map((d: Record<string, any>) => ({
    identification_type: d.idType || '',
    identification_number: d.idNumber || '',
    person_name: d.name || '',
    date_of_birth: toISODate(d.dob || ''),
    nationality: d.nationality || '',
    gender: d.gender || '',
    race: d.race || '',
    email: d.email || '',
    mobile_number: digitsOnly(d.mobile),
    office_number: digitsOnly(d.office),
    fax_number: '',
    residential_address: toAddressPayload(d._address || {}),
    is_also_member: !!d.isAlsoMember,
  }));

  // Shareholders
  const shareholdersPayload: ShareholderPayload[] = (formData.shareholders || []).map((s: Record<string, any>) => {
    const base: ShareholderPayload = {
      shareholder_type: s.type || 'Individual',
      new_or_existing: 'NEW',
      identification_number: s.idNumber || s.compRegNo || '',
      price_per_share: Number(s.pricePerShare) || 1,
      number_of_shares: Number(s.numberOfShares) || 1,
      class_of_share: s.classOfShare || 'ORDINARY',
      residential_address: toAddressPayload(s._address || {}),
    };

    if (s.type === 'BodyCorporate') {
      return {
        ...base,
        member_type: s.compRegType || undefined,
        company_registration_number: s.compRegNo || undefined,
        registration_number: s.compRegNo || undefined,
        corporate_name: s.corporateName || undefined,
        place_of_incorporation: s.placeOfInc || undefined,
      };
    }

    return {
      ...base,
      identification_type: s.idType || '',
      person_name: s.name || '',
      date_of_birth: toISODate(s.dob || ''),
      nationality: s.nationality || '',
      gender: s.gender || '',
      race: s.race || '',
      email: s.email || '',
      mobile_number: digitsOnly(s.mobile),
    };
  });

  // Information to Agency
  const haveEmployees = infoAgency.haveEmployees === 'true';

  // Branch Address (first one if exists)
  const branchAddresses = formData.branchAddresses || [];
  const branchAddr = branchAddresses.length > 0 ? toAddressPayload(branchAddresses[0]) : undefined;

  const informationToAgency: InformationToAgencyPayload = {
    do_you_have_any_employees: haveEmployees,
    number_of_full_time_local_employee: Number(infoAgency.fullTimeLocal) || 0,
    number_of_full_time_foreign_employee: Number(infoAgency.fullTimeForeign) || 0,
    number_of_part_time_local_employee: Number(infoAgency.partTimeLocal) || 0,
    number_of_part_time_foreign_employee: Number(infoAgency.partTimeForeign) || 0,
    employee_start_date: infoAgency.employeeStartDate || undefined,
    phone_number: digitsOnly(infoAgency.agencyPhone),
    fax_number: digitsOnly(infoAgency.agencyFax),
    contact_person: infoAgency.contactPerson || '',
    branch_address: branchAddr,
  };

  return {
    credential_id: credentialId,
    session_id: sessionId,
    reserved_entity_number: reservedEntityNumber || '',
    reserved_entity_name: reservedEntityName,
    business_codes: formData.businessCodes || [],
    application_details: applicationDetails,
    directors: directorsPayload,
    shareholders: shareholdersPayload,
    information_to_agency: informationToAgency,
  };
}

export function useFormPayload() {
  return { buildPayload };
}
